import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

// A hardware scanner in "keyboard wedge" mode just types the barcode's
// characters into whatever input has focus, then sends Enter. This is a
// plain text input, not a global keydown listener: simpler, testable, and
// works identically for a real scanner or a cashier typing + pressing Enter.
//
// Also offers an optional phone-camera scan path (additive - the text input
// above is completely unchanged and keeps working exactly as before). Both
// paths funnel into the same onScan(barcode) call, so callers never need to
// know which one produced a given scan.
//
// Exposes an imperative .focus() (via ref) so a parent can return focus
// here after handling a scan elsewhere (e.g. once an item has been added to
// a cart), so the cashier's next scan doesn't end up typed into whatever
// field last had focus.
const BarcodeScanInput = forwardRef(function BarcodeScanInput(
  { onScan, placeholder = "Scan or type barcode...", autoFocus = false, disabled = false },
  ref
) {
  const [value, setValue] = useState("");
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const inputRef = useRef(null);
  const html5QrcodeRef = useRef(null);
  // Unique per mount - this component renders on both Sales and Inventory,
  // and two DOM elements can never share an id.
  const viewportId = useRef(`barcode-camera-viewport-${Math.random().toString(36).slice(2)}`).current;

  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
  }));

  const submit = (rawBarcode) => {
    const barcode = (rawBarcode ?? value).trim();
    if (!barcode) return;
    onScan(barcode);
    setValue("");
    // Stay focused for the next scan by default - a caller that wants focus
    // to move elsewhere after a successful scan (e.g. to a quantity field)
    // does so itself, after onScan's own state updates land.
    inputRef.current?.focus();
  };

  // Lazy-loads html5-qrcode (a real decoding engine, not a trivial import)
  // only once the camera is actually opened - this component renders on
  // every Sales/Inventory page load, so a static top-level import would add
  // that weight to everyone's bundle even if they only ever use a hardware
  // scanner.
  useEffect(() => {
    if (!isCameraOpen) return;

    let cancelled = false;
    let instance = null;

    import("html5-qrcode")
      .then(({ Html5Qrcode }) => {
        if (cancelled) return;
        instance = new Html5Qrcode(viewportId);
        html5QrcodeRef.current = instance;
        return instance.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 250, height: 150 } },
          (decodedText) => {
            submit(decodedText);
            setIsCameraOpen(false);
          },
          () => {
            // Per-frame "no barcode found in this frame yet" callback -
            // expected on nearly every frame, not an error.
          }
        );
      })
      .catch(() => {
        if (cancelled) return;
        setCameraError("Could not access the camera - check camera permission and try again.");
        setIsCameraOpen(false);
      });

    return () => {
      cancelled = true;
      html5QrcodeRef.current = null;
      if (instance) {
        instance.stop().catch(() => {
          // already stopped, or never successfully started - safe to ignore
        });
      }
    };
  }, [isCameraOpen]);

  return (
    <div className="barcode-scan-input-wrapper">
      <div className="barcode-scan-input">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submit();
            }
          }}
          placeholder={placeholder}
          autoFocus={autoFocus}
          disabled={disabled}
        />
        <button type="button" className="btn" onClick={() => submit()} disabled={disabled}>
          Look up
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          disabled={disabled}
          onClick={() => {
            setCameraError("");
            setIsCameraOpen((open) => !open);
          }}
        >
          {isCameraOpen ? "Cancel" : "📷 Scan with camera"}
        </button>
      </div>
      {cameraError && <div className="error-msg">{cameraError}</div>}
      {isCameraOpen && <div id={viewportId} className="barcode-camera-viewport" />}
    </div>
  );
});

export default BarcodeScanInput;
